import { clsx } from 'clsx'
import { MouseEvent, ReactNode, createContext, useContext, useState } from 'react'
import { DotsThreeIcon } from './icons/16x16'
import { PopoverMenu } from './PopoverMenu'

type ContextMenuProps = {
  'children': ReactNode
  'className'?: string
  'showOnlyOnHover'?: boolean
  'aria-label'?: string
}

const MenuContext = createContext({ closeMenu: () => {} })

export function ContextMenu({
  children,
  className,
  showOnlyOnHover = true,
  'aria-label': ariaLabel = 'Menu'
}: ContextMenuProps) {
  const [isOpen, setIsOpen] = useState(false)

  const triggerClasses = clsx(
    'size-6 outline-none',
    isOpen || !showOnlyOnHover ? 'opacity-100' : 'opacity-0',
    showOnlyOnHover && 'group-hover:opacity-100 group-focus-within:opacity-100',
    className
  )

  return (
    <PopoverMenu
      isControlled={true}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      offset={{ dx: -8, dy: 0 }}
      trigger={
        <button aria-label={ariaLabel} className={triggerClasses}>
          <DotsThreeIcon aria-hidden="true" role="img" />
        </button>
      }
    >
      <div className="flex flex-col gap-y-0.5 rounded border border-solid border-stone-300 bg-white py-2 shadow outline-none">
        <MenuContext.Provider value={{ closeMenu: () => setIsOpen(false) }}>
          {children}
        </MenuContext.Provider>
      </div>
    </PopoverMenu>
  )
}

export function PrimaryContextMenu({
  children,
  'aria-label': ariaLabel = 'Menu'
}: ContextMenuProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <PopoverMenu
      isControlled={true}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      offset={{ dx: -8, dy: 0 }}
      trigger={
        <button
          aria-label={ariaLabel}
          className={clsx('size-6 outline-none')}
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()
          }}
        >
          <DotsThreeIcon aria-hidden="true" />
        </button>
      }
    >
      <div className="flex flex-col gap-y-0.5 rounded border border-solid border-stone-300 bg-white py-2 shadow outline-none">
        <MenuContext.Provider value={{ closeMenu: () => setIsOpen(false) }}>
          {children}
        </MenuContext.Provider>
      </div>
    </PopoverMenu>
  )
}

type ContextMenuItemProps = {
  'icon'?: ReactNode
  'children': ReactNode
  'onClick'?: () => void
  'className'?: string
  'aria-label'?: string
}

export function ContextMenuItem({
  icon,
  children,
  className,
  onClick,
  'aria-label': ariaLabel
}: ContextMenuItemProps) {
  const { closeMenu } = useContext(MenuContext)

  return (
    <div
      role="menuitem"
      aria-label={ariaLabel}
      onClick={(e) => {
        e.stopPropagation()
        onClick?.()
        closeMenu()
      }}
      className={clsx(
        'flex min-w-12 cursor-pointer items-center gap-2 border-l border-transparent pb-0.5 pl-1.5 pr-2 pt-0.5 text-sm text-gray-900 outline-none',
        'hover:border-l-stone-400 hover:bg-stone-200',
        className
      )}
    >
      {icon && <div className="flex w-4 flex-row items-center justify-center">{icon}</div>}
      {children}
    </div>
  )
}
